import { SvelteMap } from 'svelte/reactivity';
import { getJobEnd } from './lib';
import type { Job, SchedularVehicle, ScheduledJob } from './types';

export type OverlappingJobs = ReturnType<typeof getOverlappingJobs>;

type OverlapRange = {
    jobs: ScheduledJob[];
    start: Date;
    end: Date;
    laneCount: number;
};

export type VehicleOverlap = {
    ranges: OverlapRange[];
    jobOffsets: SvelteMap<string, number>;
    maxLaneCount: number;
};

export const getOverlappingJobs = (jobs: Map<string, Job>) => {
    const usersIdsJobs: Record<number, Job[]> = {};

    for (const job of jobs.values()) {
        if (job.vehicle === null || job.start === null) continue;
        usersIdsJobs[job.vehicle?.id] = usersIdsJobs[job.vehicle?.id] ?? [];
        usersIdsJobs[job.vehicle?.id].push(job);
    }

    const usersOverlapping: Record<number, VehicleOverlap> = {};

    for (const uid in usersIdsJobs) {
        usersOverlapping[uid] = getOverlappingJobsForUser(usersIdsJobs[uid] as ScheduledJob[]);
    }

    return usersOverlapping;
};

/**
 * Groups a vehicle's jobs into contiguous clusters of touching jobs and assigns
 * each job to a lane using first-fit interval-graph scheduling. A job is
 * placed in the lowest-indexed lane whose previous occupant has already ended
 * by the time the job starts; otherwise a new lane is allocated. The row
 * height is the maximum lane count across all clusters.
 */
export const getOverlappingJobsForUser = (jobs: ScheduledJob[]): VehicleOverlap => {
    const jobOffsets = new SvelteMap<string, number>();
    if (!jobs || jobs.length === 0) {
        return { ranges: [], jobOffsets, maxLaneCount: 0 };
    }

    jobs.sort((a, b) => a.start!.getTime() - b.start!.getTime());

    const ranges: OverlapRange[] = [];
    let currentRange: OverlapRange | null = null;
    // `laneEnds[i]` is the end time (ms since epoch) of the latest job assigned
    // to lane `i` within the current range. Reset whenever a new range starts.
    let laneEnds: number[] = [];
    let maxLaneCount = 0;

    for (const job of jobs) {
        const jobStartMs = job.start.getTime();
        const jobEnd = getJobEnd(job);
        const jobEndMs = jobEnd.getTime();

        if (currentRange === null || jobStartMs >= currentRange.end.getTime()) {
            currentRange = {
                jobs: [job],
                start: job.start,
                end: jobEnd,
                laneCount: 0,
            };
            ranges.push(currentRange);
            laneEnds = [];
        } else {
            currentRange.jobs.push(job);
            if (jobEndMs > currentRange.end.getTime()) {
                currentRange.end = jobEnd;
            }
        }

        // First-fit lane assignment: pick the lowest lane whose last job ends
        // by the time this job starts; otherwise open a new lane.
        let lane = laneEnds.findIndex((end) => end <= jobStartMs);
        if (lane === -1) {
            lane = laneEnds.length;
            laneEnds.push(jobEndMs);
        } else {
            laneEnds[lane] = jobEndMs;
        }

        jobOffsets.set(job.id, lane);
        currentRange.laneCount = Math.max(currentRange.laneCount, laneEnds.length);
        if (currentRange.laneCount > maxLaneCount) {
            maxLaneCount = currentRange.laneCount;
        }
    }

    return { ranges, jobOffsets, maxLaneCount };
};

export const getJobOffset = (job: Job, vehicle: SchedularVehicle | undefined, overlappingJobs: OverlappingJobs): number => {
    if (job.vehicle?.id !== vehicle?.id) {
        throw new Error(
            `Called get job offset for job at wrong vehicle, job id:${job.id}, vehicle id: ${vehicle?.id}, jobs user id: ${job.vehicle_id}`,
        );
    }
    return overlappingJobs[vehicle!.id]?.jobOffsets.get(job.id) ?? 0;
};
